// packages/web-ui/src/AppShell/useDestinationKeybindings.ts
//
// W2-S3 Task 5 (AC #3) — global keyboard map for destination switching.
//   Ctrl+1 → chat, Ctrl+2 → kb, Ctrl+3 → files, Ctrl+4 → views, Ctrl+5 → diff,
//   Ctrl+6 → git, Ctrl+7 → skills, Ctrl+, → settings, F1 → open help popover
//   (custom event `tessa:openHelp`).
//
// Cross-platform Cmd vs Ctrl — `event.ctrlKey || event.metaKey` per
// `packages/extension/src/views/lean-chat-panel.ts`.
//
// The hook MUST be invoked exactly once at the AppShell root (single source of
// truth). Focused inputs win precedence — typing `Ctrl+1` in a textarea does
// NOT navigate (Pitfall #16).

import { useEffect } from 'react';
import { navigateTo, type HashRoute } from '../router/hash-router.js';
import { openNewChat } from '../state/open-new-chat.js';

/** Custom event dispatched when the user presses F1. `<HelpPopover>` listens. */
export const HELP_OPEN_EVENT = 'tessa:openHelp';

// Note: Ctrl+1 (chat) is handled specially below — it opens a fresh
// conversation via `openNewChat()` rather than routing to bare `/chat`.
const ROUTE_FOR_KEY: Record<string, HashRoute> = {
  '2': { kind: 'kb' },
  '3': { kind: 'files' },
  '4': { kind: 'views' },
  '5': { kind: 'diff' },
  '6': { kind: 'git' },
  '7': { kind: 'skills' },
  ',': { kind: 'settings' },
};

/** Returns true when the keyboard event originated inside an editable element. */
function isInEditableTarget(event: KeyboardEvent): boolean {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return false;
  if (target.matches('input, textarea, [contenteditable="true"]')) return true;
  return target.closest('input, textarea, [contenteditable="true"]') !== null;
}

/**
 * Registers a global `keydown` listener that maps Ctrl/Cmd+1..7, Ctrl/Cmd+,
 * and F1 onto navigation actions. Listener is attached on mount and removed
 * on unmount — no leaks.
 *
 * The hook intentionally uses a stable inline handler with an empty dep
 * array: re-creating the listener on every render adds/removes ~60×/sec on
 * resize and leaks under StrictMode (Pitfall #18).
 */
export function useDestinationKeybindings(): void {
  useEffect(() => {
    function handler(event: KeyboardEvent): void {
      // Focused inputs always win. The master keyboard map (W2-S10) will
      // refine precedence; for this story, focused inputs win unconditionally.
      if (isInEditableTarget(event)) return;

      // F1 → open help popover (no Ctrl/Meta required).
      if (event.key === 'F1') {
        event.preventDefault();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(HELP_OPEN_EVENT));
        }
        return;
      }

      // All other bindings require Ctrl (or Cmd on macOS).
      if (!(event.ctrlKey || event.metaKey)) return;
      // Skip when other modifiers are also held — Ctrl+Shift+1 is reserved.
      if (event.altKey || event.shiftKey) return;

      // Ctrl+1 → open a fresh conversation (mirrors the activity-bar Chat
      // button), not the bare `/chat` placeholder route.
      if (event.key === '1') {
        event.preventDefault();
        void openNewChat();
        return;
      }

      const route = ROUTE_FOR_KEY[event.key];
      if (route === undefined) return;

      event.preventDefault();
      navigateTo(route);
    }

    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, []);
}
